import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export interface AppState {
  /**
   * Backs the Network Error banner docs/ui/design-system.md §18 specifies
   * ("a persistent, non-dismissing banner... the user's in-progress context
   * should never be destroyed by a connectivity blip") — the one piece of
   * truly app-wide state that isn't Authentication, Theme, or Socket, and
   * has a real, already-documented UI behavior driving it, rather than
   * being spec'd speculatively.
   */
  isOnline: boolean;
}

const initialState: AppState = {
  isOnline: typeof navigator === "undefined" ? true : navigator.onLine,
};

/**
 * Reducer-only, like the other slices — nothing here subscribes to the
 * browser's `online`/`offline` events yet. That listener is a provider-level
 * side effect (it belongs next to `StoreProvider`/`ThemeProvider`, the same
 * way `ThemeProvider` owns the `matchMedia` subscription for theme), not
 * something a slice module should set up as an import-time side effect.
 */
const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },
  },
});

export const { setOnlineStatus } = appSlice.actions;
export default appSlice.reducer;
